import { useState } from "react";
import { Download } from "lucide-react";
import { sendWrite } from "../../offline/writeQueue";
import { track } from "../../lib/events";
import { inputClass, primaryButtonClass, textareaClass } from "../ui";
import { LocalLink } from "../../offline/LocalMedia";
import { ManualCompleteButton } from "./ManualCompleteButton";
import { useCompletion } from "./useCompletion";
import { contentOf } from "./types";
import type { PracticalActivityContent, ReflectionContent, RendererProps } from "./types";

export function PracticalActivityRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<PracticalActivityContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [queued, setQueued] = useState(false);

  async function submit() {
    const result = await sendWrite({
      method: "POST",
      path: `/api/teacher/activities/${item.id}/responses`,
      body: { text_value: text },
    });
    setQueued(result.queued);
    track("form_submitted", {
      module_id: moduleID,
      item_id: item.id,
      payload: { kind: "practical_activity" },
    });
    setSubmitted(true);
    completion.reportResponseSubmitted();
  }

  const collectsResponse =
    content.response_type === "short_text" || content.response_type === "long_text";

  return (
    <section className="grid gap-4">
      <div
        className="text-base leading-relaxed text-app-ink"
        dangerouslySetInnerHTML={{ __html: content.instructions || "" }}
      />
      {(content.files || []).length > 0 && (
        <div className="divide-y divide-app-line rounded-md border border-app-line">
          {(content.files || []).map((file) => (
            <LocalLink
              key={file.id || file.file_name}
              className="flex items-center gap-2 p-3 text-sm font-medium text-accent"
              href={file.file_url}
              fileName={file.file_name}
              target="_blank"
              rel="noreferrer"
            >
              <Download size={14} />
              {file.file_name}
            </LocalLink>
          ))}
        </div>
      )}
      {collectsResponse ? (
        submitted || completion.done ? (
          <p className="text-sm text-success-text">
            {queued
              ? "Response saved on this device — it will sync when you're online."
              : "Response submitted."}
          </p>
        ) : (
          <>
            {content.response_type === "long_text" ? (
              <textarea
                className={textareaClass}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            ) : (
              <input
                className={inputClass}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            )}
            <button
              className={primaryButtonClass}
              onClick={() => void submit()}
              disabled={!text.trim()}
            >
              Submit
            </button>
          </>
        )
      ) : (
        <ManualCompleteButton
          done={completion.done}
          onClick={() => void completion.complete()}
          rule={item.completion_rule}
        />
      )}
    </section>
  );
}

export function ReflectionRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<ReflectionContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [queued, setQueued] = useState(false);

  async function submit() {
    const result = await sendWrite({
      method: "POST",
      path: `/api/teacher/reflections/${item.id}/responses`,
      body: { response_text: text },
    });
    setQueued(result.queued);
    track("form_submitted", {
      module_id: moduleID,
      item_id: item.id,
      payload: { kind: "reflection" },
    });
    setSubmitted(true);
    completion.reportResponseSubmitted();
  }

  const tooShort = Boolean(content.min_length && text.length < content.min_length);
  const tooLong = Boolean(content.max_length && text.length > content.max_length);

  return (
    <section className="grid gap-4">
      <p className="text-base leading-relaxed text-app-ink">{content.prompt}</p>
      {submitted || completion.done ? (
        <p className="text-sm text-success-text">
          {queued
            ? "Response saved on this device — it will sync when you're online."
            : "Response saved."}
        </p>
      ) : (
        <>
          {content.response_type === "long_text" ? (
            <textarea
              className={`${textareaClass} min-h-32`}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          ) : (
            <input
              className={inputClass}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          )}
          <button
            className={primaryButtonClass}
            onClick={() => void submit()}
            disabled={!text.trim() || tooShort || tooLong}
          >
            Submit
          </button>
        </>
      )}
    </section>
  );
}
